import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Building2, ChevronLeft, ChevronRight, Search, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import { fetchAssociationOptions } from '../services/taskApi';

const TYPE_CONFIG = {
  contact: {
    title: 'Associate Contacts',
    empty: 'No contacts found',
    icon: Users,
  },
  opportunity: {
    title: 'Associate Opportunities',
    empty: 'No opportunities found',
    icon: Users,
  },
  company: {
    title: 'Associate Companies',
    empty: 'No companies found',
    icon: Building2,
  },
};

const PAGE_SIZE = 20;

function associationKey(item) {
  return `${item.objectType}:${item.objectId}`;
}

function uniqueAssociations(items) {
  return Array.from(
    new Map(items.map((item) => [associationKey(item), item])).values(),
  );
}

export function normalizeAssociationOption(objectType, item) {
  return {
    objectType,
    objectId: item.id,
    displayName: item.display_name,
    secondaryText: item.secondary_text,
    metadata: item.metadata || {},
  };
}

function ResultDetails({ objectType, item }) {
  const metadata = item.metadata || {};

  if (objectType === 'contact') {
    return (
      <>
        <span>{item.secondary_text || 'No email'}</span>
        {metadata.phone ? <span>{metadata.phone}</span> : null}
        {metadata.workspace_label ? <span>{metadata.workspace_label}</span> : null}
      </>
    );
  }

  if (objectType === 'opportunity') {
    return (
      <>
        {metadata.contact_name ? <span>{metadata.contact_name}</span> : null}
        {metadata.pipeline_name || metadata.stage_name ? (
          <span>{[metadata.pipeline_name, metadata.stage_name].filter(Boolean).join(' · ')}</span>
        ) : null}
        <span>
          {[metadata.status || item.secondary_text, metadata.formatted_value].filter(Boolean).join(' · ')}
        </span>
      </>
    );
  }

  return (
    <>
      <span>{metadata.domain || item.secondary_text || 'No domain'}</span>
      {metadata.phone ? <span>{metadata.phone}</span> : null}
      {metadata.primary_contact_name ? <span>{metadata.primary_contact_name}</span> : null}
    </>
  );
}

export function TaskAssociationSelector({
  open,
  onOpenChange,
  objectType,
  workspace,
  selectedAssociations = [],
  onApply,
  returnFocusRef,
}) {
  const config = TYPE_CONFIG[objectType] || TYPE_CONFIG.contact;
  const Icon = config.icon;
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState([]);
  const [retryNonce, setRetryNonce] = useState(0);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const requestSequence = useRef(0);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setPage(1);
    setDraft(uniqueAssociations(
      selectedAssociations.filter((item) => item.objectType === objectType),
    ));
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, objectType, selectedAssociations]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open || !objectType) return undefined;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');

    fetchAssociationOptions(workspace, objectType, {
      search: debouncedQuery,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((data) => {
        if (sequence !== requestSequence.current) return;
        setItems(data.items || []);
        setTotalItems(data.total_items || 0);
        setTotalPages(
          data.total_pages
          || Math.max(1, Math.ceil((data.total_items || 0) / (data.page_size || PAGE_SIZE))),
        );
      })
      .catch((requestError) => {
        if (sequence !== requestSequence.current) return;
        setItems([]);
        setTotalItems(0);
        setTotalPages(1);
        setError(
          requestError?.response?.data?.detail
          || `Unable to load ${objectType}s. Try again.`,
        );
      })
      .finally(() => {
        if (sequence === requestSequence.current) setLoading(false);
      });

    return () => {
      requestSequence.current += 1;
    };
  }, [open, objectType, workspace, debouncedQuery, page, retryNonce]);

  const draftKeys = useMemo(
    () => new Set(draft.map(associationKey)),
    [draft],
  );

  const toggleItem = (item) => {
    const normalized = normalizeAssociationOption(objectType, item);
    const key = associationKey(normalized);
    setDraft((current) => (
      current.some((entry) => associationKey(entry) === key)
        ? current.filter((entry) => associationKey(entry) !== key)
        : [...current, normalized]
    ));
  };

  const close = () => {
    onOpenChange(false);
    window.setTimeout(() => returnFocusRef?.current?.focus(), 0);
  };

  const handleRowKeyDown = (event, index, item) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      rowRefs.current[Math.min(index + 1, items.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      rowRefs.current[Math.max(index - 1, 0)]?.focus();
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      toggleItem(item);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent
        priority
        hideClose
        className="flex h-[min(760px,calc(100dvh-1rem))] w-[calc(100vw-1rem)] max-w-3xl flex-col overflow-hidden p-0 sm:h-[min(720px,calc(100vh-3rem))]"
        aria-describedby={`task-association-description-${objectType}`}
        data-testid={`task-association-selector-${objectType}`}
      >
        <DialogHeader className="shrink-0 border-b border-border px-4 py-4 text-left sm:px-6">
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription id={`task-association-description-${objectType}`}>
            Search this workspace and select one or more records.
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 border-b border-border px-4 py-3 sm:px-6">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${objectType}s`}
              className="pl-9"
              aria-label={`Search ${objectType}s`}
              data-testid={`task-association-search-${objectType}`}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
            {draft.length} selected
          </p>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-2 py-2 sm:px-4"
          role="listbox"
          aria-label={`${config.title} results`}
          aria-multiselectable="true"
          aria-busy={loading}
        >
          {loading ? (
            <div
              className="flex h-full min-h-40 items-center justify-center text-sm text-muted-foreground"
              role="status"
              data-testid="task-association-loading"
            >
              Loading results…
            </div>
          ) : error ? (
            <div
              className="flex h-full min-h-40 flex-col items-center justify-center gap-3 px-4 text-center"
              role="alert"
              data-testid="task-association-error"
            >
              <AlertCircle className="h-6 w-6 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRetryNonce((current) => current + 1)}
              >
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div
              className="flex h-full min-h-40 flex-col items-center justify-center gap-2 px-4 text-center"
              data-testid="task-association-empty"
            >
              <Icon className="h-7 w-7 text-muted-foreground" />
              <p className="font-medium">{config.empty}</p>
              <p className="text-sm text-muted-foreground">
                {debouncedQuery ? 'Try a different search.' : 'No records are available in this workspace.'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item, index) => {
                const normalized = normalizeAssociationOption(objectType, item);
                const selected = draftKeys.has(associationKey(normalized));
                return (
                  <div
                    key={item.id}
                    ref={(node) => { rowRefs.current[index] = node; }}
                    role="option"
                    aria-selected={selected}
                    tabIndex={index === 0 ? 0 : -1}
                    onClick={() => toggleItem(item)}
                    onKeyDown={(event) => handleRowKeyDown(event, index, item)}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3 outline-none transition-colors',
                      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:border-border hover:bg-muted/50',
                    )}
                    data-testid={`task-association-result-${item.id}`}
                  >
                    <Checkbox
                      checked={selected}
                      aria-label={`Select ${item.display_name}`}
                      tabIndex={-1}
                      className="mt-1 pointer-events-none"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.display_name || item.id}</p>
                      <div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-3">
                        <ResultDetails objectType={objectType} item={item} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-border bg-background px-4 py-3 sm:px-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {totalItems} {totalItems === 1 ? 'result' : 'results'} · Page {page} of {totalPages}
            </p>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={loading || page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                aria-label="Previous results page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={loading || page >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                aria-label="Next results page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter className="flex-row justify-end gap-2 space-x-0">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                onApply(draft);
                close();
              }}
              data-testid="task-association-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TaskAssociationSelector;
